/**
 * paymentStartError.js
 * Turns a failed /payment/paymentIntents call into words for the front desk.
 *
 * The server refuses some card payments before Stripe is ever touched (e.g. a
 * self-employed stylist whose payouts aren't set up). It answers with
 * statusCode !== 1 and error.responseMessage — or axios rejects on a non-2xx —
 * and the checkout used to swallow both, leaving a dead Charge button.
 */

const SE_NOT_READY = /self employed staff member has not added their bank account/i;
const OWNER_NOT_READY = /hairdresser has not added their bank account/i;

function serverMessage(resOrErr) {
  const body = resOrErr?.response?.data || resOrErr?.data || null;
  const err = body?.error;
  if (typeof err === 'string') return err;
  return err?.errors?.responseMessage || err?.responseMessage || err?.message || body?.message || (resOrErr instanceof Error ? resOrErr.message : '') || '';
}

/** A short, actionable message for a payment that couldn't be started. */
export function paymentStartError(resOrErr, staffName) {
  const msg = serverMessage(resOrErr);
  const first = (staffName || '').trim().split(' ')[0];
  if (SE_NOT_READY.test(msg)) {
    return `${first || 'This stylist'} hasn't finished setting up payouts with Stripe, so card payments can't be taken for them yet. Take cash or another method, or ask them to finish setup in Settings › Payouts.`;
  }
  if (OWNER_NOT_READY.test(msg)) {
    return "The salon hasn't finished setting up payouts with Stripe, so card payments can't be taken yet. Take cash or another method, or finish setup in Settings › Payouts.";
  }
  return msg || "Couldn't start the card payment. Please try again.";
}

/** Throws a readable Error unless the paymentIntents response succeeded. */
export function assertPaymentStarted(response, staffName) {
  if (response?.data?.statusCode === 1) return response;
  throw new Error(paymentStartError(response, staffName));
}

/** Dispatches paymentIntents and throws a readable Error on any failure. */
export async function startPaymentIntent(dispatchCall, staffName) {
  let response;
  try {
    response = await dispatchCall();
  } catch (e) {
    throw new Error(paymentStartError(e, staffName));
  }
  return assertPaymentStarted(response, staffName);
}
